import { createFeatureSelector, createSelector } from '@ngrx/store';
import { LeadPipelineState } from './lead-pipeline.state';

export const selectLeadPipelineState = createFeatureSelector<LeadPipelineState>('leadPipeline');

// ===========================================================================
// List
// ===========================================================================
export const selectListState = createSelector(selectLeadPipelineState, s => s.list);
export const selectListItems = createSelector(selectListState, l => l.items);
export const selectListMeta = createSelector(selectListState, l => l.meta);
export const selectListStatus = createSelector(selectListState, l => l.status);
export const selectListFilters = createSelector(selectListState, l => l.filters);
export const selectListTabs = createSelector(selectListState, l => l.tabs);
export const selectListIncludeArchived = createSelector(selectListState, l => l.includeArchived);
export const selectListIsLoading = createSelector(selectListStatus, s => s === 'loading');
export const selectListIsEmpty = createSelector(
  selectListState,
  l => l.status === 'success' && l.items.length === 0,
);

// ===========================================================================
// Detail
// ===========================================================================
export const selectDetailState = createSelector(selectLeadPipelineState, s => s.detail);
export const selectDetailCurrent = createSelector(selectDetailState, d => d.current);
export const selectDetailStatus = createSelector(selectDetailState, d => d.status);

// ===========================================================================
// Dashboard
// ===========================================================================
export const selectDashboardState = createSelector(selectLeadPipelineState, s => s.dashboard);
export const selectDashboardTiles = createSelector(selectDashboardState, d => d.tiles);
export const selectDashboardChart = createSelector(selectDashboardState, d => d.chart);
export const selectDashboardChartRange = createSelector(selectDashboardState, d => d.chartRange);
export const selectDashboardStatus = createSelector(selectDashboardState, d => d.status);

// ===========================================================================
// Companies
// ===========================================================================
export const selectCompaniesState = createSelector(selectLeadPipelineState, s => s.companies);
export const selectCompaniesItems = createSelector(selectCompaniesState, c => c.items);
export const selectCompaniesMeta = createSelector(selectCompaniesState, c => c.meta);
export const selectCompaniesStatus = createSelector(selectCompaniesState, c => c.status);
export const selectCompaniesFilters = createSelector(selectCompaniesState, c => c.filters);
export const selectCompaniesHasMore = createSelector(
  selectCompaniesState,
  c => c.meta.current_page < c.meta.last_page,
);

// ===========================================================================
// Persons
// ===========================================================================
export const selectPersonsState = createSelector(selectLeadPipelineState, s => s.persons);
export const selectPersonsItems = createSelector(selectPersonsState, p => p.items);
export const selectPersonsMeta = createSelector(selectPersonsState, p => p.meta);
export const selectPersonsStatus = createSelector(selectPersonsState, p => p.status);
export const selectPersonsFilters = createSelector(selectPersonsState, p => p.filters);
export const selectPersonsUnlockingIds = createSelector(selectPersonsState, p => p.unlockingIds);
export const selectPersonsHasMore = createSelector(
  selectPersonsState,
  p => p.meta.current_page < p.meta.last_page,
);
export const selectIsUnlockingEmail = (personUuid: string) =>
  createSelector(selectPersonsUnlockingIds, ids => ids.includes(personUuid));

// ===========================================================================
// Qualified leads
// ===========================================================================
export const selectLeadsState = createSelector(selectLeadPipelineState, s => s.leads);
export const selectLeadsItems = createSelector(selectLeadsState, l => l.items);
export const selectLeadsMeta = createSelector(selectLeadsState, l => l.meta);
export const selectLeadsStatus = createSelector(selectLeadsState, l => l.status);
export const selectLeadsFilters = createSelector(selectLeadsState, l => l.filters);
export const selectSelectedLead = createSelector(selectLeadsState, l => l.selectedLead);
export const selectLeadDrawerStatus = createSelector(selectLeadsState, l => l.drawerStatus);
export const selectLeadsHasMore = createSelector(
  selectLeadsState,
  l => l.meta.current_page < l.meta.last_page,
);

// ===========================================================================
// Activity
// ===========================================================================
export const selectActivityState = createSelector(selectLeadPipelineState, s => s.activity);
export const selectActivityItems = createSelector(selectActivityState, a => a.items);
export const selectActivityMeta = createSelector(selectActivityState, a => a.meta);
export const selectActivityStatus = createSelector(selectActivityState, a => a.status);
export const selectActivityFilters = createSelector(selectActivityState, a => a.filters);
export const selectActivityHasMore = createSelector(
  selectActivityState,
  a => a.meta.current_page < a.meta.last_page,
);

// ===========================================================================
// Runs
// ===========================================================================
export const selectRunsState = createSelector(selectLeadPipelineState, s => s.runs);
export const selectRunsItems = createSelector(selectRunsState, r => r.items);
export const selectRunsMeta = createSelector(selectRunsState, r => r.meta);
export const selectRunsStatus = createSelector(selectRunsState, r => r.status);
export const selectCurrentRun = createSelector(selectRunsState, r => r.currentRun);
export const selectRunsHasMore = createSelector(
  selectRunsState,
  r => r.meta.current_page < r.meta.last_page,
);

// ===========================================================================
// Notifications
// ===========================================================================
export const selectNotificationsState = createSelector(selectLeadPipelineState, s => s.notifications);
export const selectNotificationsItems = createSelector(selectNotificationsState, n => n.items);
export const selectNotificationsUnreadCount = createSelector(selectNotificationsState, n => n.unreadCount);
export const selectNotificationsStatus = createSelector(selectNotificationsState, n => n.status);

// ===========================================================================
// Exports
// ===========================================================================
export const selectExportsState = createSelector(selectLeadPipelineState, s => s.exports);
export const selectExportsItems = createSelector(selectExportsState, e => e.items);
export const selectExportsStatus = createSelector(selectExportsState, e => e.status);
export const selectExportsCreating = createSelector(selectExportsState, e => e.creating);

// ===========================================================================
// Wizard
// ===========================================================================
export const selectWizardState = createSelector(selectLeadPipelineState, s => s.wizard);
export const selectWizardStep = createSelector(selectWizardState, w => w.step);
export const selectWizardMode = createSelector(selectWizardState, w => w.mode);
export const selectWizardEditStatus = createSelector(selectWizardState, w => w.editStatus);
/** Steps 1–2 are read-only while editing a pipeline that is not paused (spec §1 / §4.2). */
export const selectWizardLocked = createSelector(
  selectWizardState,
  w => w.mode === 'edit' && w.editStatus !== null && w.editStatus !== 'paused',
);
/** True while editing a paused pipeline — surfaces the "editing filters resets pagination" warning. */
export const selectWizardEditingPaused = createSelector(
  selectWizardState,
  w => w.mode === 'edit' && w.editStatus === 'paused',
);
export const selectWizardFilters = createSelector(selectWizardState, w => w.filters);
export const selectWizardLeadsPerDay = createSelector(selectWizardState, w => w.leadsPerDay);
export const selectWizardWorkspaceId = createSelector(selectWizardState, w => w.workspaceId);
export const selectWizardTitle = createSelector(selectWizardState, w => w.title);
export const selectWizardDescription = createSelector(selectWizardState, w => w.description);
export const selectWizardSessionId = createSelector(selectWizardState, w => w.sessionId);
export const selectWizardSubmitting = createSelector(selectWizardState, w => w.submitting);
export const selectWizardSubmitError = createSelector(selectWizardState, w => w.submitError);
export const selectPreviewCounts = createSelector(selectWizardState, w => w.previewCounts);
export const selectPreviewStatus = createSelector(selectWizardState, w => w.previewStatus);
export const selectPreviewError = createSelector(selectWizardState, w => w.previewError);
export const selectPreviewRateLimitRetryAfter = createSelector(
  selectWizardState,
  w => w.previewRateLimitRetryAfter,
);
export const selectUniquePayloadsSent = createSelector(selectWizardState, w => w.uniquePayloadsSent);
/** True once at least one filter condition carries a value — gates the wizard step-1 Continue button. */
export const selectWizardHasFilters = createSelector(selectWizardFilters, filters =>
  (filters ?? []).some(c => {
    const v = c?.value;
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === 'object') return Object.values(v).some(x => x !== null && x !== undefined && x !== '');
    return v !== null && v !== undefined && v !== '';
  }),
);

// ===========================================================================
// Run-now
// ===========================================================================
export const selectRunNowState = createSelector(selectLeadPipelineState, s => s.runNow);
export const selectRunNowConfirmModalOpen = createSelector(selectRunNowState, r => r.confirmModalOpen);
export const selectRunNowInFlightMap = createSelector(selectRunNowState, r => r.inFlight);
export const selectIsRunNowInFlight = (uuid: string) =>
  createSelector(selectRunNowInFlightMap, map => !!map[uuid]);
